import type { CSSProperties } from "react";
import RedemptionHeader from "@/components/giftExperience/RedemptionHeader";
import RedemptionCollection from "@/components/redemption/RedemptionCollection";
import RedemptionFooter from "@/components/redemption/RedemptionFooter";
import RedemptionHero from "@/components/redemption/RedemptionHero";
import RedemptionPledgeBanner from "@/components/redemption/RedemptionPledgeBanner";
import { AVAILABLE_COUNTRIES } from "@/constants/campaignBuilder";
import { useRedemptionProducts } from "@/hooks/useRedemptionProducts";
import type { GiftContext } from "@/interfaces/redemption";

interface RedemptionLandingProps {
  gift: GiftContext;
  isMobile: boolean;
  /** The gift-message + video modals live at the Redemption level; the hero just
   *  requests them via these callbacks. */
  onReadMessage: () => void;
  onPlayVideo: () => void;
  /** Card tap → open the product-detail slide-in (owned by Redemption, like the modals). */
  onOpenProduct: (productId: string) => void;
  /** Destination country for the header's "Shipping to" picker + the feed filter — lifted to
   *  Redemption so the recipient-details stage uses the same country. Changeable only when
   *  shipping is unlocked. */
  activeCountry: string;
  onCountryChange: (code: string) => void;
}

/**
 * Recipient Landing — the branded page shown after the unbox video ends, composed
 * top-to-bottom: header (GfG + optional partner logo + the top-right "Shipping to"
 * picker) → hero (message / banner / video, RFW-3) → gift collection grid + filters
 * (RFW-4) → donate-unredeemed pledge banner + footer (RFW-5), all driven by `gift`.
 * Modals are owned by Redemption (top level), not here.
 */
export default function RedemptionLanding({
  gift,
  isMobile,
  onReadMessage,
  onPlayVideo,
  onOpenProduct,
  activeCountry,
  onCountryChange,
}: RedemptionLandingProps) {
  const { config } = gift;
  // Feed load state gates the pledge banner + footer (GFG-346). Cached — shares the one fetch with
  // the collection.
  const { isLoading: feedLoading } = useRedemptionProducts(gift.token);

  return (
    <div
      className="absolute inset-0 overflow-y-auto"
      // --lp-accent (Pro accent) recolors the active gift-filter tab + product cause;
      // undefined ⇒ the shared components keep their default tokens.
      style={
        { background: "var(--color-white)", "--lp-accent": config.accentColor } as CSSProperties
      }
    >
      {/* Header — GfG logo + optional partner logo + the top-right "Shipping to" picker
          (locked to US unless the campaign allows international shipping). When unlocked the
          recipient may pick any country on the shipping allowlist (AVAILABLE_COUNTRIES); the
          feed then filters to what ships there. We deliberately do NOT narrow the list to the
          countries the current gifts happen to ship to — that collapsed it to US + the active
          country whenever the catalog's shippableCountries were sparse. */}
      <RedemptionHeader
        partnerLogoUrl={config.showLogo ? config.logoUrl : undefined}
        partnerLogoAlt={gift.sender.companyName ?? "Partner"}
        shipTo={{
          country: activeCountry,
          onChange: onCountryChange,
          locked: gift.shipping?.locked ?? false,
          countries: AVAILABLE_COUNTRIES,
        }}
      />

      <RedemptionHero
        config={config}
        isMobile={isMobile}
        onReadMessage={onReadMessage}
        onPlayVideo={onPlayVideo}
        headline={config.heroTitle}
        subtitle={config.heroSubtitle}
        bgColor={config.heroBgColor}
        textMode={config.heroTextMode}
      />

      {/* Gift collection grid + filters (RFW-4) — below the hero. Card tap bubbles the
          product id up to open the detail slide-in (RFW-5), owned by Redemption. The active
          country (from the header picker) filters the feed by product shippability. */}
      <RedemptionCollection
        isMobile={isMobile}
        token={gift.token}
        onOpenProduct={onOpenProduct}
        activeCountry={activeCountry}
      />

      {/* Donate-unredeemed charity call-out (RFW-5) — only when the sender chose to
          donate unclaimed gift value and a beneficiary is set. Held back (with the footer) until
          the feed settles, so the grid landing doesn't shove them down (GFG-346). */}
      {!feedLoading && config.donateUnredeemed && gift.beneficiary && (
        <RedemptionPledgeBanner beneficiary={gift.beneficiary} sender={gift.sender} />
      )}

      {/* Footer — GfG logo + mission + certs; sits at the foot of the page. Shown once the feed
          settles (success or error). */}
      {!feedLoading && <RedemptionFooter />}
    </div>
  );
}
